/**
 * Direct analyst dispatch for the companion chat (2026-08-24).
 *
 * Market-data questions used to ride chat-model -> ask_analyst tool ->
 * chat-model re-summary: ~10s, the analyst's ack event silently dropped,
 * streaming only on the final re-write. This route detects data questions
 * client-side (mirroring the server catalogs' vocabulary) and streams the
 * analyst SSE straight into the conversation: ack bubble ~1s, first answer
 * tokens ~2-3s, and the analyst's text IS the reply.
 *
 * Detection is deliberately conservative: an action-verb match always wins
 * (the chat model owns workspace actions), and a miss just means the old
 * chat path — never a wrong behavior, only a slower one.
 */
import type {
  ChatChoice,
  DirectDataRoute,
  EntityType,
  PendingWire,
  TurnReadingWire,
} from '@edge/edge-ai'
import {
  ACTION_RE,
  addKnown,
  ALLOWANCE_SPENT_MESSAGE,
  AllowanceSpentError,
  detectDataQuestion,
  EDGESCRIPT_RE,
  ENTITY_TYPES,
  extractTicker,
  oneOf,
} from '@edge/edge-ai'
import { companionClient } from '@/services/api/api-client'
import { ApiError } from '@/services/api/errors'
import { parseSseStream } from '@/lib/parse-sse-stream'
import { createAuraSpeech } from '@/components/chat/voice/aura-stream-speech'
import { createPcmPlayer } from '@/components/chat/voice/pcm-player'
import { clampTierToPlan } from '@/features/companion/answer-tiers'
import { getResolvedPlanTier } from '@/stores/plan-tier-store'
import { useTierStore } from '@/stores/tier-store'
import { useThreadStore } from '@/stores/thread-store'
import { onScreenWatchlists } from '@/features/companion/screen-watchlists'
import { useLanguageStore, forcedLanguageName } from '@/stores/language-store'
import { VOICE_ANALYST_TIER } from '@/features/companion/voice-style'
import { splitMentionPreamble } from '@/components/chat/mention-preamble'
import { currentTurnPanels } from '@/features/companion/turn-panels'
import { EDGESCRIPT_ENABLED } from '@/features/edgescript/flags'
import { FLAGS } from '@/services/analytics/feature-flags'
import { isFeatureFlagOn } from '@/services/analytics/use-feature-flag'

// Detection (DATA_RE / ACTION_RE / LEARNING_RE, extractTicker,
// detectDataQuestion) lives in @edge/edge-ai (core/data-question.ts) so the
// admin Router Lab exercises the SAME first node of the routing tree.
// Re-exported here for existing imports and tests.
export { detectDataQuestion, extractTicker } from '@edge/edge-ai'

/** Server caps (edge-companion-api analyst/router.ts AnalystAskRequestSchema). */
const ANALYST_QUESTION_MAX = 1000
const ANALYST_CONTEXT_MAX = 8000

/** Server caps (analyst/conversation-context.ts HistorySchema): 8 turns, 4000 chars each. */
const HISTORY_MAX_TURNS = 6
const HISTORY_MAX_CHARS = 1500

/**
 * The last few turns for the analyst: @-mention preambles stripped (the
 * panels ride as data), each turn trimmed. Long answers keep their opening,
 * which carries the tickers and the verdict.
 */
export function analystHistory(
  history: StreamArgs['history'],
): Array<{ role: 'user' | 'assistant'; text: string }> {
  return (history ?? [])
    .map((t) => ({
      role: t.role,
      text: (t.role === 'user' ? splitMentionPreamble(t.text).body : t.text)
        .trim()
        .slice(0, HISTORY_MAX_CHARS),
    }))
    .filter((t) => t.text !== '')
    .slice(-HISTORY_MAX_TURNS)
}

interface StreamArgs {
  question: string
  ticker?: string
  modality: 'text' | 'voice'
  signal?: AbortSignal
  history?: Array<{ role: 'user' | 'assistant'; text: string }>
  /** The thread's pending slot the question answers; null when nothing is pending. */
  pending?: PendingWire | null
  /** The turn reading the route check returned (phase 6); absent when the turn skipped it. */
  reading?: TurnReadingWire | null
  /** The companion turn's id, sent as `client_turn_id` so the analyst's events join the turn. */
  clientTurnId?: string
  onAck(text: string): void
  onDelta(text: string): void
  /** The server's answer guard withdrew the text so far; the fix follows. */
  onReset?(): void
}

/** Lean analyst SSE client: relays ack + answer_delta, resolves final text. */
export async function streamAnalystForChat(args: StreamArgs): Promise<{
  text: string
  answerEventId?: string
  choices?: ChatChoice[]
  resolves?: EntityType
  /** What the analyst's model rounds reported (`round_usage`), for the turn's ai_turn record. */
  usage?: { rounds: number; promptTokens: number | null; cachedTokens: number | null }
}> {
  const storedTier = useTierStore.getState().tier
  // Voice always runs FAST: a spoken answer waits on generation + playback,
  // and the server's spoken contract is short anyway — Heavy-tier depth in
  // the ear is all latency, no benefit. The effort chip governs chat only.
  const effectiveTier =
    args.modality === 'voice'
      ? VOICE_ANALYST_TIER
      : clampTierToPlan(storedTier, getResolvedPlanTier())

  let response: Response
  try {
    // @-mentioned panels: their DATA travels as structured `panels`, which
    // the server treats as fetched evidence (analyst/panel-evidence.ts) and
    // scopes the answer to. They stay attached for follow-ups in the same
    // conversation (turn-panels.ts). `question` carries only what the user
    // asked; earlier turns ride in `history` so "they" / "that day" resolve.
    // Plain-text `context` remains only as a fallback with no panel data.
    const { body: asked, context } = splitMentionPreamble(args.question)
    const panels = currentTurnPanels()
    const history = analystHistory(args.history)
    const threadId = useThreadStore.getState().activeThreadId
    const screenLists = onScreenWatchlists()
    const body = {
      question: asked.trim() || args.question.slice(-ANALYST_QUESTION_MAX),
      ...(panels?.length
        ? { panels }
        : context
          ? { context: context.slice(0, ANALYST_CONTEXT_MAX) }
          : {}),
      ...(history.length ? { history } : {}),
      // What the question answers (spec 2026-10-18, 6.2): the analyst reads
      // whether it asked from the slot, not the text. Null: nothing pending.
      pending: args.pending ?? null,
      // The route check's reading, untouched: the analyst reads it in place of its
      // capability call (edge-companion-api turn-reading/wire.ts).
      ...(args.reading ? { reading: args.reading } : {}),
      // The chat thread, so analytics files the answer under its
      // conversation ($ai_session_id). Analytics only.
      ...(threadId ? { session_id: threadId } : {}),
      // The companion turn, so the analyst's events join it (analytics only).
      ...(args.clientTurnId ? { client_turn_id: args.clientTurnId } : {}),
      // "My watchlist" is the one on screen when there is one (watchlist tools).
      ...(screenLists.length ? { on_screen_watchlists: screenLists } : {}),
      ticker: args.ticker,
      // Voice gets the server's spoken contract (short, no markdown);
      // text keeps the comprehensive table-friendly one.
      modality: args.modality,
      language: forcedLanguageName(),
      tier: effectiveTier,
      // The server offers EdgeScript (its tools, the edgescript capability) only to a
      // request that says so: sent inside the build gate, for `edgescript-release`.
      ...(EDGESCRIPT_ENABLED && isFeatureFlagOn(FLAGS.EDGESCRIPT) ? { edgescript: true } : {}),
    }
    response = args.signal
      ? await companionClient.stream('/v1/analyst/ask/stream', body, { signal: args.signal })
      : await companionClient.stream('/v1/analyst/ask/stream', body)
  } catch (err) {
    if (err instanceof ApiError && err.status === 402) {
      throw new AllowanceSpentError(ALLOWANCE_SPENT_MESSAGE)
    }
    if (err instanceof ApiError) {
      throw new Error(`The analyst is unavailable right now (${err.status}).`)
    }
    throw err
  }
  if (!response.body) {
    throw new Error(`The analyst is unavailable right now (${response.status}).`)
  }

  let fullText = ''
  let answerEventId: string | undefined
  let errorMessage: string | undefined
  let choices: ChatChoice[] | undefined
  let resolves: EntityType | undefined
  let rounds = 0
  let promptTokens: number | null = null
  let cachedTokens: number | null = null

  for await (const { event, data } of parseSseStream(response.body)) {
    // Data that is not a JSON object is a malformed frame: skip it.
    if (!data || typeof data !== 'object') continue
    const payload = data as Record<string, unknown>
    if (event === 'ack' && typeof payload.text === 'string') {
      args.onAck(payload.text)
    } else if (event === 'answer_delta' && typeof payload.text === 'string') {
      fullText += payload.text
      args.onDelta(payload.text)
    } else if (event === 'answer_reset') {
      // The server's answer guard withdrew the draft; the fix follows.
      fullText = ''
      args.onReset?.()
    } else if (event === 'choices' && Array.isArray(payload.options)) {
      // What the answer asks the trader to pick from: buttons in the chat.
      choices = (payload.options as unknown[]).filter(
        (o): o is ChatChoice =>
          !!o && typeof o === 'object' && typeof (o as ChatChoice).label === 'string',
      )
      // What a pick resolves (a watchlist), for the thread's pending slot.
      resolves = oneOf(ENTITY_TYPES, payload.resolves) ? payload.resolves : undefined
    } else if (event === 'round_usage') {
      // What each model round reported, for the turn's ai_turn record.
      rounds += 1
      promptTokens = addKnown(promptTokens, payload.tokens_in)
      cachedTokens = addKnown(cachedTokens, payload.cached_tokens)
    } else if (event === 'done') {
      answerEventId =
        typeof payload.answer_event_id === 'string' ? payload.answer_event_id : undefined
    } else if (event === 'error') {
      errorMessage = typeof payload.message === 'string' ? payload.message : 'Analyst error'
    }
  }

  if (errorMessage) throw new Error(errorMessage)
  if (!fullText) throw new Error('The analyst returned no answer.')
  return {
    text: fullText,
    answerEventId,
    ...(choices?.length ? { choices, ...(resolves ? { resolves } : {}) } : {}),
    ...(rounds > 0 ? { usage: { rounds, promptTokens, cachedTokens } } : {}),
  }
}

// ── live voice speech (Aura speak-stream) ────────────────────────────────────

type SpeechSession = ReturnType<typeof createAuraSpeech>

/** Test seam: builds the per-answer speech session. */
export type SpeechSessionFactory = (language: string | null) => SpeechSession

/** The DirectDataRoute the app layout hands to CompanionProvider. */
export function buildDirectAnalystRoute(speechFactory?: SpeechSessionFactory): DirectDataRoute {
  // One live speech session at a time; barge-in and new questions clear it.
  let activeSpeech: SpeechSession | null = null
  const stopSpeech = () => {
    activeSpeech?.cancel()
    activeSpeech?.close()
    activeSpeech = null
  }

  return {
    // No modes (2026-10-15): this is the regex verdict, sent to the turn check
    // as `route_hint`; the server's Jev router decides, on every plan.
    // EdgeScript questions are the exception and stream from the analyst at
    // once (unambiguous keywords, and Jev's turn types have no script type).
    detect: (raw) => {
      // Detect on what the user asked, not the @-mention panel snapshot.
      const text = splitMentionPreamble(raw).body
      if (EDGESCRIPT_RE.test(text)) return { ticker: extractTicker(text), direct: true }
      return detectDataQuestion(text)
    },
    // The change-request fallback for the turn check (Phase 2b), on what the
    // user asked, not the @-mention panel snapshot.
    changeHint: (raw) => ACTION_RE.test(splitMentionPreamble(raw).body),
    cancelSpeech: stopSpeech,
    stream: async (args) => {
      let speech: SpeechSession | null = null
      if (args.modality === 'voice') {
        stopSpeech()
        try {
          const language = useLanguageStore.getState().language
          speech = speechFactory
            ? speechFactory(language)
            : // Default tokenProvider reads the live Cognito access token
              // synchronously (WS auth rides the bearer subprotocol).
              createAuraSpeech({ language, sink: createPcmPlayer() })
          activeSpeech = speech
        } catch (e) {
          console.warn('[companion] live speech unavailable, falling back to clip TTS', e)
          speech = null
        }
      }

      // A reset means the server replaced the draft (a key said in words, advice
      // removed). What was spoken cannot be taken back, so the rest of the answer
      // is shown but not fed to the voice.
      let spokenReset = false
      try {
        const result = await streamAnalystForChat({
          ...args,
          onAck: (ack) => {
            // Feed the ack into the speech session first (a forced
            // sentence boundary so it speaks immediately), then the UI.
            speech?.onDelta(ack.replace(/[.…\s]+$/u, '') + '. ')
            args.onAck(ack)
          },
          onDelta: (delta) => {
            if (!spokenReset) speech?.onDelta(delta)
            args.onDelta(delta)
          },
          onReset: () => {
            if (speech) spokenReset = true
            args.onReset?.()
          },
        })
        speech?.onDone()
        return { ...result, spokenLive: speech !== null }
      } catch (e) {
        // Failed answers should not keep talking.
        if (speech) stopSpeech()
        throw e
      }
    },
  }
}
